import dayjs from 'dayjs'
import { useFormik } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import Loader from '../../../components/Loader'
import Message from '../../../components/Message'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import Input from '../../../components/v2/Input'
import MovingPrice from '../../../components/v2/MovingPrice'
import PhoneNumberInput from '../../../components/v2/PhoneNumberInput'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Button from '../../../components/v2/buttons/Button'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import BaseModal from '../../../components/v2/modals/BaseModal'
import PetProgressbar from '../../../components/v2/progrees-bars/PetProgressBar'
import { RegisterPetPolicy } from '../../../domain/types'
import { RootState, useAppSelector } from '../../../store'
import { AppState, clearPhoneInfo } from '../../../store/appSlice'
import { AuthState } from '../../../store/authSlice'
import { PetState, setInsurerInfo, setOrderId, setTotalPrice } from '../../../store/petSlice'
import { Helmet } from 'react-helmet-async'

const PetPolicyRegistration = () => {
  //
  const dispatch = useDispatch()
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const { checkout, screenHeader, filter } = useAppSelector((data: { pet: PetState }) => data.pet)
  //
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  //
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetPetPageInfoQuery('pet-your-contact-info')
  //
  const pageTitle = pageInfo?.translations[lang]?.title
  const stepName = pageInfo?.translations[lang]?.stepName
  //
  const [openNewPassModal, setOpenNewPassModal] = useState<boolean>(false)
  //
  //
  // yup validations schema GE
  const ContactValidationSchema = Yup.object({
    email: Yup.string()
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .email(`${t('enterValidMail')}`)
      .required(`${t('required')}`),
    phone: Yup.string()
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      // .min(9, `${t('enterValidPhone')}`)
      // .max(9, `${t('enterValidPhone')}`)
      .required(`${t('required')}`),
    password: Yup.string()
      .min(8, `${t('minPassLenght')}`)
      .matches(/[0-9]/, `${t('passContainNumber')}`)
      .matches(/[a-z]/, `${t('passContainLowerCase')}`)
      .matches(/[a-zA-Z]/, `${t('passContainEnglishLetters')}`)
      .matches(/[A-Z]/, `${t('passContainUpperCase')}`),
    confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], `${t('enterSamePass')}`),
    smsCode: Yup.string()
      .min(4, `${t('enterFourDigitCode')}`)
      .max(4, `${t('enterFourDigitCode')}`)
      .required(`${t('enterFourDigitCode')}`),
  })
  //
  const MainValidationSchema = ContactValidationSchema
  //
  // initial values
  const initialValues = {
    email: checkout.insurer.email || '',
    phone: checkout.insurer.phone || '',
    password: checkout.insurer.password || '',
    confirmPassword: checkout.insurer.confirmPassword || '',
    smsCode: checkout.insurer.smsCode || '',
  }
  // formi state
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: MainValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: (values) => {
      console.log(values)
    },
  })
  //
  //
  // send verification sms mutation
  const [verificationSms, { isLoading: isverificationSmsNumberLoading, isSuccess: verificationSmsNumberSuccess }] =
    api.useSendVerificationSmsMutation()
  //
  const [
    policyRegistration,
    { data: policyRegistrationResponse, isLoading: isPolicyRegistrationLoading, isError: isPolicyRegistrationError },
  ] = api.usePetPolicyGenerationMutation()
  //
  //
  const setOrderInfoCallBack = useCallback(() => {
    if (policyRegistrationResponse && policyRegistrationResponse.id && policyRegistrationResponse.totalPrice !== null) {
      return [
        dispatch(setOrderId(policyRegistrationResponse.id)),
        dispatch(setTotalPrice(policyRegistrationResponse.totalPrice)),
        navigate('/pet-payment'),
        dispatch(clearPhoneInfo()),
      ]
    }
    //
    if (policyRegistrationResponse && !policyRegistrationResponse.id) {
      toast.error(`${t('failedToSaveOrderId')}`)
    }
    //
    if (policyRegistrationResponse && policyRegistrationResponse.totalPrice === null) {
      toast.error(`${t('failedToSaveTotalPrice')}`)
    }
    //
  }, [policyRegistrationResponse, dispatch, navigate, t])
  //
  //
  useEffect(() => {
    setOrderInfoCallBack()
    //
    return () => {}
  }, [setOrderInfoCallBack])
  //
  //
  // new user
  useEffect(() => {
    //
    if (
      // authSmsVerificationError &&
      (auth.user === null || auth.user) &&
      checkout.insurer.smsCode &&
      checkout.insurer.phone &&
      checkout.insurer.smsCode.length === 4 &&
      checkout.insurer.email &&
      checkout.insurer.password
    ) {
      const new_user = { email: checkout.insurer.email, password: checkout.insurer.password }
      //
      localStorage.setItem('new_user', JSON.stringify(new_user))
    }
    //
    return () => {}
  }, [
    checkout.insurer.password,
    checkout.insurer.email,
    checkout.insurer.phone,
    checkout.insurer.userHaveAccount,
    // authSmsVerificationError,
    auth.user,
    checkout.insurer.smsCode,
  ])
  //
  const _policyRegistration = async () => {
    //
    // string date convertor to date
    const convertDateFormat = (inputDate?: string) => {
      if (inputDate !== undefined) {
        const parts = inputDate.split(' / ')
        if (parts.length === 3) {
          const day = parts[0]
          const month = parts[1]
          const year = parts[2]
          const formattedDate = `${year}-${month}-${day}`
          return formattedDate
        } else {
          return 'Invalid date format'
        }
      }
    }
    //
    // insurer date format
    const insurerBirthday = convertDateFormat(checkout.insurer.dateOfbirth as unknown as string)
    //
    // policy start & end dates
    const startDate = filter.dates && filter.dates.start && dayjs(filter.dates.start).format('YYYY-MM-DDTHH:mm:ss.SSS')
    const endDate = filter.dates && filter.dates.end && dayjs(filter.dates.end).format('YYYY-MM-DDTHH:mm:ss.SSS')
    //
    const promoCode =
      filter.promoCode === '' ? null : filter.discountUsedSuccessfully === false ? null : filter.promoCode
    // pet information object
    const petInformationObject: RegisterPetPolicy['petInformation'] = {
      name: filter.petInfo.name as string,
      age: {
        year: filter.petInfo.age as number,
        month: filter.petInfo.month as number,
      },
      color: filter.petInfo.color as string,
      gender: filter.petInfo.gender as string,
    }
    //
    // terms
    const termsObj: RegisterPetPolicy['clientInformation']['terms'] = {
      consentTerm: checkout.insurer.consentTerm ?? false,
      policyTerm: checkout.insurer.policyTerm ?? false,
      privacyTerm: checkout.insurer.privacyTerm ?? false,
    }
    //
    const cultureInfo: RegisterPetPolicy['culture'] = {
      lang: lang,
    }
    //
    // insurer information
    const insurerInformation: RegisterPetPolicy['clientInformation'] = {
      terms: termsObj,
      citizenship: checkout.insurer.cityzenship ?? '',
      birthday: insurerBirthday ?? '',
      privateNumber: checkout.insurer.idNumber ?? '',
      gender: checkout.insurer.gender ?? '',
      phoneNumber: checkout.insurer.phone ?? '',
      email: checkout.insurer.email ?? '',
      password: checkout.insurer.password ?? '',
      sgsVerification: checkout.insurer.sgsVerification ?? false,
      translations: checkout.insurer.translations as RegisterPetPolicy['clientInformation']['translations'],
    }
    //
    //
    if (filter.offer && filter.dates && startDate && endDate && insurerInformation && petInformationObject) {
      //
      try {
        await policyRegistration({
          paymentTerm: filter.seletedPaymentMethod.type,
          breedId: filter.breedId,
          otherBreedName: filter.petInfo.breedOther,
          animalCategoryId: filter.petCategoryId as number,
          paymentScheduleTypeId: filter.offer.paymentScheduleType.id,
          productId: filter.offer.package.productId,
          petPackageId: filter.offer.package.petPackage.id,
          compensationLimit: filter.offer.package.compensationLimit,
          compensationCurrency: filter.offer.currency,
          price: filter.offer.totalPaymentAmount,
          smsCode: checkout.insurer.smsCode,
          startDate: startDate,
          endDate: endDate,
          culture: cultureInfo,
          clientInformation: insurerInformation,
          petInformation: petInformationObject,
          petPriceId: filter.offer.petPriceId,
          promoCode: promoCode,
        }).unwrap()
      } catch (error: any) {
        //
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message

          // if sms does not match
          if (errorMessage === 'SMS_CODE_DOES_NOT_MATCH') {
            toast.info(`${t('smsDoesNotMatch')}`)
            //
          } else if (errorMessage === 'USER_NOT_FOUND') {
            // user does not match
            toast.info(`${t('registerUserNotFound')}`)
            //
          } else if (errorMessage === 'USER_WITH_PRIVATE_NUMBER_ALREADY_EXISTS') {
            // this personal number alredy exist
            toast.info(`${t('personalNumberAlreadyExist')}`)
            //
          } else if (errorMessage === 'USER_WITH_EMAIL_ALREADY_EXISTS') {
            // this email alredy exist
            toast.info(`${t('emailAlreadyExist')}`)
            //
          } else if (errorMessage === 'USER_WITH_PHONE_NUMBER_ALREADY_EXISTS') {
            // this phone number alredy exist
            toast.info(`${t('phoneAlreadyExist')}`)
            //
          } else {
            toast.error(errorMessage)
          }
          //
          //
        } else if (error?.data?.message === 'PROMO_CODE_ERROR') {
          toast.error(`${t('promoCodeCantbeUsed')}`)
        } else {
          toast.error(`${t('anErrorOccurred')} PetPolicyRegistration.tsx`)
        }
        //
      }
      //
    }
    //
  }
  // send verification sms number
  const sendVerificationSmsFunction = async () => {
    if (checkout.insurer.phone && checkout.insurer.email) {
      try {
        //
        await verificationSms({ phone: checkout.insurer.phone }).unwrap()
        //
        toast.success(`${t('smsSuccessfullySent')}`)
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('unknownError')} PolicyRegistration.tsx`)
        }
      }
    } else if (checkout.insurer.email) {
      toast.info(`${t('enterValidMail')}`)
    }
  }
  //
  //
  //
  // open password modal
  useEffect(() => {
    if (
      isPolicyRegistrationError &&
      // @ts-ignore
      isPolicyRegistrationError?.data?.errors?.[0]?.message === 'USER_DOES_NOT_EXIST' &&
      (auth.user === null || auth.user) &&
      checkout.insurer.smsCode &&
      checkout.insurer.phone &&
      checkout.insurer.email &&
      checkout.insurer.smsCode.length === 4
    ) {
      setOpenNewPassModal(true)
    }
    //

    return () => {}
  }, [auth.user, isPolicyRegistrationError, checkout.insurer.smsCode, checkout.insurer.phone, checkout.insurer.email])
  //
  // look logged user data
  const setLoggedUserCallBack = useCallback(() => {
    if (auth.user?.phone && auth?.user?.userType === 'User' && auth.user?.email) {
      return [
        dispatch(
          setInsurerInfo({
            fieldName: 'phone',
            fieldValue: auth.user?.phone,
          }),
        ),
        dispatch(
          setInsurerInfo({
            fieldName: 'email',
            fieldValue: auth.user?.email,
          }),
        ),
      ]
    }
  }, [auth?.user?.userType, auth.user?.phone, auth.user?.email, dispatch])
  //
  //
  // set logged user data in rtk store
  useEffect(() => {
    //
    setLoggedUserCallBack()
    //
    return () => {}
  }, [setLoggedUserCallBack])
  //
  // new user
  useEffect(() => {
    //
    if (
      isPolicyRegistrationError &&
      // @ts-ignore
      isPolicyRegistrationError?.data?.errors?.[0]?.message === 'USER_DOES_NOT_EXIST' &&
      (auth.user === null || auth.user) &&
      checkout.insurer.smsCode &&
      checkout.insurer.phone &&
      checkout.insurer.smsCode.length === 4 &&
      checkout.insurer.email &&
      checkout.insurer.password
    ) {
      const new_user = { email: checkout.insurer.email, password: checkout.insurer.password }
      //
      localStorage.setItem('new_user', JSON.stringify(new_user))
    }
    //
    return () => {}
  }, [
    checkout.insurer.password,
    checkout.insurer.email,
    checkout.insurer.phone,
    checkout.insurer.userHaveAccount,
    isPolicyRegistrationError,
    auth.user,
    checkout.insurer.smsCode,
  ])
  //
  //
  // auto navigate first page
  useEffect(() => {
    if (filter?.petCategoryId === null) {
      navigate('/choose-pet')
    }
    //
    return () => {}
  }, [filter?.petCategoryId, navigate])
  //
  const MainLoading = isPageInfoLoading || isPolicyRegistrationLoading || isverificationSmsNumberLoading
  //
  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <BaseModal isOpen={openNewPassModal} onClose={() => setOpenNewPassModal(!openNewPassModal)}>
        <NewAccountModalBlock>
          <h5>{t('simpleRegistration')}</h5>
          <p>{t('createYourAccount')}</p>
          <Input
            id="pet-policy-registration-password"
            name="pet-policy-registration-password"
            label={t('enterNewPassword')}
            type="password"
            icon={true}
            placeholder=""
            value={checkout.insurer.password || ''}
            onChange={(value) => [
              dispatch(
                setInsurerInfo({
                  fieldName: 'password',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('password', value),
            ]}
            width={'fullWidth'}
            error={formik.errors.password && checkout.insurer.password ? true : false}
            errorText={formik.errors.password}
            onBlur={formik.handleBlur}
            style={{
              marginTop: '24px',
            }}
          />

          <Input
            id="pet-policy-confirm-registration-password"
            name="pet-policy-confirm-registration-password"
            label={t('repeatNewPassword')}
            type="password"
            icon={true}
            placeholder=""
            value={checkout.insurer.confirmPassword || ''}
            onChange={(value) => [
              dispatch(
                setInsurerInfo({
                  fieldName: 'confirmPassword',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('confirmPassword', value),
            ]}
            width={'fullWidth'}
            error={formik.errors.confirmPassword && checkout.insurer.confirmPassword ? true : false}
            errorText={formik.errors.confirmPassword}
            onBlur={formik.handleBlur}
            style={{
              marginTop: '24px',
            }}
          />

          <Message
            style={{
              marginTop: '10px',
              marginBottom: '0px',
            }}
            variant="gray"
            text={`${t('passwordText')}`}
          />

          <Button
            fullWidth={true}
            style={{
              marginTop: '48px',
            }}
            disabled={
              checkout.insurer.password &&
              checkout.insurer.confirmPassword &&
              checkout.insurer.password === checkout.insurer.confirmPassword &&
              checkout.insurer.consentTerm
                ? false
                : true
            }
            buttonType="primary"
            text={t('registrationAndContinue')}
            onClick={() => {
              if (
                checkout.insurer.password &&
                checkout.insurer.confirmPassword &&
                checkout.insurer.password === checkout.insurer.confirmPassword &&
                checkout.insurer.consentTerm
              ) {
                //
                _policyRegistration()
              }
            }}
          />
        </NewAccountModalBlock>
      </BaseModal>

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />

      <Container>
        <Content>
          <Title text={pageTitle} />

          <PetProgressbar stepName={stepName} />

          <MovingPrice price={filter.offerTotalPrice} currency="GEL" />

          <WhiteBox>
            <Input
              id="pet-policy-registration-email"
              name="pet-policy-registration-email"
              label={`${t('email')}`}
              type="email"
              placeholder=""
              value={checkout.insurer.email || ''}
              onChange={(value) => [
                dispatch(
                  setInsurerInfo({
                    fieldName: 'email',
                    fieldValue: value,
                  }),
                ),
                formik.setFieldValue('email', value),
              ]}
              width={'fullWidth'}
              error={formik.errors.email && checkout.insurer.email ? true : false}
              errorText={formik.errors.email}
              onBlur={formik.handleBlur}
              disabled={
                checkout.insurer.email &&
                checkout.insurer.phone &&
                checkout.insurer.smsCode &&
                verificationSmsNumberSuccess &&
                checkout.insurer.smsCode.length === 4
                  ? true
                  : false
              }
            />

            <PhoneNumberInput
              style={{
                marginTop: '24px',
              }}
              id="pet-policy-registration-phone"
              label={`${t('phone')}`}
              value={checkout.insurer.phone || ''}
              onChange={(value) => [
                dispatch(
                  setInsurerInfo({
                    fieldName: 'phone',
                    fieldValue: value,
                  }),
                ),
                formik.setFieldValue('phone', value),
              ]}
              resendOnclick={sendVerificationSmsFunction}
              sendSms={sendVerificationSmsFunction}
              error={formik.errors.phone && checkout.insurer.phone ? true : false}
              errorText={formik.errors.phone}
              onBlur={formik.handleBlur}
              hideButtons={false}
              disabled={
                (checkout.insurer.email &&
                  checkout.insurer.phone &&
                  checkout.insurer.smsCode &&
                  verificationSmsNumberSuccess &&
                  checkout.insurer.smsCode.length === 4) ||
                isverificationSmsNumberLoading ||
                (policyRegistrationResponse &&
                  policyRegistrationResponse.id &&
                  policyRegistrationResponse.totalPrice) ||
                formik.errors.email
                  ? true
                  : false
              }
            />

            {(verificationSmsNumberSuccess && checkout.insurer.phone && checkout.insurer.email) ||
            (checkout.insurer.phone && checkout.insurer.email && checkout.insurer.smsCode) ? (
              <Input
                id="pet-policy-registration-phone-sms"
                name="pet-policy-registration-phone=sms"
                label={`${t('smsCode')}`}
                type="number"
                placeholder=""
                value={checkout.insurer.smsCode || ''}
                onChange={(value) => [
                  dispatch(
                    setInsurerInfo({
                      fieldName: 'smsCode',
                      fieldValue: value,
                    }),
                  ),
                  formik.setFieldValue('smsCode', value),
                ]}
                width={'fullWidth'}
                error={formik.errors.smsCode && checkout.insurer.smsCode ? true : false}
                errorText={formik.errors.smsCode}
                onBlur={formik.handleBlur}
                disabled={
                  policyRegistrationResponse && policyRegistrationResponse.id && policyRegistrationResponse.totalPrice
                    ? true
                    : false
                }
                style={{
                  marginTop: '24px',
                }}
              />
            ) : null}
          </WhiteBox>

          <Buttons>
            <NavigationButton
              text={`${t('back')}`}
              buttonType="prev"
              onClick={() => navigate('/pet-check-information')}
            />
            <NavigationButton
              text={`${t('next')}`}
              buttonType="next"
              disabled={
                (checkout.insurer.email &&
                  checkout.insurer.phone &&
                  checkout.insurer.smsCode &&
                  checkout.insurer.smsCode.length === 4) ||
                (checkout.insurer.email &&
                  checkout.insurer.phone &&
                  checkout.insurer.smsCode &&
                  checkout.insurer.smsCode.length === 4 &&
                  checkout.insurer.password &&
                  checkout.insurer.confirmPassword &&
                  checkout.insurer.password === checkout.insurer.confirmPassword)
                  ? false
                  : true
              }
              //
              onClick={() => {
                if (
                  // if user don't have account & open create password modal & create new user profile
                  isPolicyRegistrationError &&
                  // @ts-ignore
                  isPolicyRegistrationError?.data?.errors?.[0]?.message === 'USER_DOES_NOT_EXIST' &&
                  (auth.user === null || auth.user) &&
                  checkout.insurer.smsCode &&
                  checkout.insurer.phone &&
                  checkout.insurer.email &&
                  checkout.insurer.smsCode.length === 4
                ) {
                  setOpenNewPassModal(true)
                }
                //
                //
                if (
                  //  if user have account & policy registration
                  (checkout.insurer.email && checkout.insurer.phone && checkout.insurer.smsCode) ||
                  (checkout.insurer.password &&
                    checkout.insurer.confirmPassword &&
                    checkout.insurer.password === checkout.insurer.confirmPassword &&
                    checkout.insurer.smsCode &&
                    checkout.insurer.smsCode.length === 4 &&
                    checkout.insurer.phone &&
                    formik.isValid)
                ) {
                  //
                  _policyRegistration()
                  //
                }
                //
              }}
            />{' '}
          </Buttons>
        </Content>
      </Container>
      <BackgroundIcon />
    </Wrapper>
  )
}
//
//
const NewAccountModalBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  h5 {
    font-size: 24px;
    color: ${Colors.primaryBlack};
  }
  p {
    font-size: 14px;
    color: ${Colors.nevadaGrey};
  }
`

const WhiteBox = styled.div`
  background-color: ${Colors.white};
  margin-block: 32px;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  max-width: 464px;
  width: 100%;
  margin-inline: auto;
  border-radius: 8px;
  padding: 48px;

  ${Responsive.mobile} {
    padding: 20px;
  }
`
const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`
const Content = styled.div`
  padding-block: 60px;
  position: sticky;
  z-index: 1;

  ${Responsive.mobile} {
    padding-block: 30px;
  }
`
const Wrapper = styled.div`
  /*  */
`
//
export default PetPolicyRegistration
